import type { JSX } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConsentGate } from '@/shared/components/ConsentGate';
import { useCandidateByShareId } from '../hooks/useCandidateByShareId';
import { candidateAppService } from '../services/candidate-app.service';
import { CandidateSessionProvider } from '../context/CandidateSessionContext';
import { CandidateAppLayout } from './CandidateAppLayout';

export function AnonymousCandidateWrapper(): JSX.Element {
  const { shareId } = useParams<{ shareId: string }>();
  const queryClient = useQueryClient();
  const { candidate, isLoading, isError } = useCandidateByShareId(shareId ?? '');

  const handleConsent = useCallback(async (): Promise<void> => {
    try {
      await candidateAppService.giveConsent(shareId ?? '');
      await queryClient.invalidateQueries({ queryKey: ['candidate-public', shareId] });
    } catch {
      toast.error('Failed to record consent. Please try again.');
    }
  }, [shareId, queryClient]);

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Link Error</CardTitle>
            <CardDescription>Candidate not found.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (candidate && !candidate.consentGiven) {
    return <ConsentGate variant="candidate" onConsent={() => void handleConsent()} />;
  }

  return (
    <CandidateSessionProvider mode="anonymous" shareId={shareId}>
      <CandidateAppLayout />
    </CandidateSessionProvider>
  );
}
